import type { OnboardingEvent, OnboardingState } from '@developerz/domain';
import type { JSX } from 'solid-js';
import type { IntendedTier } from '../lib/onboarding';
import { GH_APP_INSTALL_URL } from '../lib/onboarding';
import { useOrgKeyGate } from '../lib/org-key-gate';
import { loadedRepos, useRepoList } from '../lib/repos';
import { OnboardingBoxStep } from './OnboardingBoxStep';
import { OnboardingPanel } from './OnboardingPanel';
import { RepoEnablementPanel } from './RepoEnablementPanel';

// Step container: the one read the funnel card needs beyond `/state`.
//
// Mounted only while onboarding is UNFINISHED (the route swaps in the done card
// at `active`), so the repo walk starts when a step that consumes it is on
// screen rather than on every visit — the `<CommandPalette/>` rule. Four of the
// eight step states read it: "Select repos" needs it to know whether there is
// anything to select, "Merge the setup PR" and "Validate" to point at the repos
// the file has to land on, and `invalid` to NAME the repo whose policy was
// refused.
//
// Two more reads joined it in epic #3629, and both stay HERE for that same
// reason: the org-key gate (both key pools, so the key step can say why a
// personal key satisfies nothing) and the live roster behind the enrol-a-server
// step, which is handed down as a rendered slot.
//
// Split from `<OnboardingPanel/>` rather than folded into it so the presenter
// stays prop-driven and every state can be rendered in a test without a query
// client — which is what `OnboardingPanel.states.test.tsx` walks the enum with.
export type OnboardingStepCardProps = {
  readonly token: string | null;
  readonly state: OnboardingState;
  /** This card is ahead of the stored ladder: its Continue re-reads rather than advances. */
  readonly displayOnly: boolean;
  /** 1-based rail position and the rail's length, for "STEP n OF N". */
  readonly step: number;
  readonly stepCount: number;
  readonly busy: boolean;
  readonly onAdvance: (event: OnboardingEvent, intendedTier?: IntendedTier) => void;
  readonly onCopy: (text: string) => void;
  readonly onRecheck: () => void;
};

export function OnboardingStepCard(props: OnboardingStepCardProps): JSX.Element {
  const repos = useRepoList(props.token);
  const personalKeysOnly = useOrgKeyGate(props.token);
  return (
    <OnboardingPanel
      state={props.state}
      displayOnly={props.displayOnly}
      step={props.step}
      stepCount={props.stepCount}
      busy={props.busy}
      ghAppInstallUrl={GH_APP_INSTALL_URL}
      // `undefined` while in flight or after a refusal — never `?? []`, which
      // would let a 500 claim the account has no repos and send the user off to
      // enable one that already is.
      repos={loadedRepos(repos)}
      reposFailed={repos.view.isError}
      onRetryRepos={() => repos.reload()}
      onAdvance={props.onAdvance}
      onCopy={props.onCopy}
      onRecheck={props.onRecheck}
      personalKeysOnly={personalKeysOnly()}
      // A slot rather than a branch inside the presenter: the step is a live
      // roster over two enrollment transports, and that is a query.
      boxStep={<OnboardingBoxStep token={props.token} />}
      reposStep={
        <RepoEnablementPanel
          token={props.token}
          onChanged={() => {
            repos.reload();
            props.onRecheck();
          }}
        />
      }
    />
  );
}
